import type { BaseBlock } from "@/react/components/shared/BuilderUI/DataModel.types";
import type { ElementType, TextElement } from "@/react/types/Element";
import type { Form } from "@/react/types/Form";

import { elementToBlock } from "./elementToBlock";

// Recursively converts elements and their children to BuilderUI blocks
const convertElementWithChildren = (
  element: ElementType,
  fieldIndexRef: { current: number },
  skipProfileFieldTypes: boolean,
  isFirstChild = false,
): BaseBlock => {
  // Convert the element to a block
  let block: BaseBlock;
  if (element.type === "field") {
    block = elementToBlock(
      element,
      fieldIndexRef.current,
      skipProfileFieldTypes,
    );
    fieldIndexRef.current++;
  } else {
    block = elementToBlock(element);
  }
  block.meta = { ...block.meta, isFirstChild };

  // Only layout elements can have children
  if (
    element.type === "layout" &&
    element.children &&
    element.children.length > 0
  ) {
    const sortedChildren = [...element.children].filter(
      child =>
        // Filter out hidden fields from old editor
        !(
          child.type === "field" &&
          "hidden" in child.attributes &&
          child.attributes.hidden
        ),
    );

    const childBlocks = sortedChildren.map((child, index) =>
      convertElementWithChildren(
        child as ElementType,
        fieldIndexRef,
        skipProfileFieldTypes,
        index === 0,
      ),
    );

    // Update the block's children
    block.props.children = {
      prop: "children",
      type: "children",
      value: childBlocks,
    };
  }

  return block;
};

// Converts a form to BuilderUI block structure
export const formToBlocks = (
  form: Form,
  excludeElement?: TextElement,
  skipProfileFieldTypes = false,
): BaseBlock => {
  // Only process top-level elements (parent_element_id === null)
  const topLevelElements = [...form.elements]
    .filter(element => {
      // Filter out top-level elements that shouldn't be shown
      if (element.parent_element_id !== null) return false;
      // Filter out hidden fields from old editor
      if (element.type === "field" && element.attributes.hidden) return false;
      // Filter out specific element if provided (e.g., title element for modal rendering)
      return !(excludeElement && element.id === excludeElement.id);
    })
    .sort((a, b) => a.position - b.position);

  const fieldIndexRef = { current: 0 };
  const childBlocks = topLevelElements.map((element, index) =>
    convertElementWithChildren(
      element,
      fieldIndexRef,
      skipProfileFieldTypes,
      index === 0,
    ),
  );

  return {
    id: `form-root-${form.id}`,
    type: "root",
    props: {
      children: {
        prop: "children",
        type: "children",
        value: childBlocks,
      },
      className: {
        prop: "other",
        type: "string",
        value: "!bg-transparent !h-auto",
      },
    },
  };
};
